<template>
  <section class="mealim-analysis-details">
    <button type="button" class="mealim-analysis-details__trigger"
      :aria-expanded="open ? 'true' : 'false'" :aria-controls="panelId" @click="toggle">
      <span>{{ open ? "Masquer l'analyse" : "Comprendre le score MEALIM" }}</span>
      <span aria-hidden="true">{{ open ? "−" : "+" }}</span>
    </button>
    <div v-if="open" :id="panelId" class="mealim-analysis-details__body">
      <p v-if="loading" class="mealim-message" role="status">Chargement de l’analyse MEALIM…</p>
      <p v-else-if="error" class="mealim-message mealim-message--error" role="alert">{{ error }}</p>
      <div v-else-if="analysis && analysis.status === 'missing_ig'" class="mealim-message">
        <p>L’analyse complète n’est pas disponible pour cette recette, car il manque une valeur d’index glycémique.</p>
        <p v-if="analysis.missing_ig && analysis.missing_ig.length">Valeur manquante pour : {{ analysis.missing_ig.join(", ") }}.</p>
      </div>
      <div v-else-if="analysis && analysis.status === 'ok'">
        <p v-if="analysis.tip" class="mealim-tip">{{ analysis.tip }}</p>
        <div class="mealim-grid">
          <article v-for="item in analysis.diagnostics || []" :key="item.label"
            class="mealim-diagnostic" :class="'mealim-diagnostic--' + item.status">
            <span class="mealim-icon" aria-hidden="true">{{ statusIcon(item.status) }}</span>
            <div class="mealim-diagnostic__body">
              <div class="mealim-heading">
                <h4>{{ item.label }}</h4>
                <span class="mealim-status">{{ statusLabel(item.status) }}</span>
              </div>
              <p>{{ item.text }}</p>
              <details v-if="item.details">
                <summary>Pourquoi ce critère ? Comment progresser ?</summary>
                <p>{{ item.details }}</p>
              </details>
            </div>
          </article>
        </div>
        <p class="mealim-note">Analyse selon les quantités de référence de la recette, sans adaptations personnelles.</p>
      </div>
      <p v-else class="mealim-message">L’analyse MEALIM n’est pas disponible pour le moment.</p>
    </div>
  </section>
</template>
<script>
import { api } from "@/api";
export default {
  name: "MealimAnalysisDetails",
  props: { dishId: { type: [String, Number], required: true } },
  data() { return { open: false, loading: false, error: "", analysis: null }; },
  computed: { panelId() { return "mealim-analysis-panel-" + this._uid; } },
  watch: {
    dishId() { this.analysis = null; this.error = ""; if (this.open) this.loadAnalysis(); },
  },
  methods: {
    async toggle() {
      this.open = !this.open;
      if (this.open && !this.analysis && !this.loading) await this.loadAnalysis();
    },
    async loadAnalysis() {
      if (this.dishId == null) return;
      this.loading = true; this.error = "";
      const requestedId = String(this.dishId);
      try {
        const response = await api.get("/recipes/" + requestedId + "/mealim-analysis/");
        if (String(this.dishId) === requestedId) this.analysis = response.data;
      } catch (error) {
        this.error = error.response?.data?.detail || "Impossible de charger l’analyse MEALIM pour le moment.";
      } finally { this.loading = false; }
    },
    statusLabel(status) {
      return status === "limited" ? "Repère limité" : status === "good" ? "Favorable" : status === "watch" ? "À surveiller" : "À améliorer";
    },
    statusIcon(status) { return status === "limited" ? "i" : status === "good" ? "✓" : status === "watch" ? "↗" : "!"; },
  },
};
</script>
<style scoped>
.mealim-analysis-details { margin-top: 14px; }
.mealim-analysis-details__trigger {
  display:flex; width:100%; align-items:center; justify-content:space-between; gap:12px;
  border:1px solid #dce8d8; border-radius:10px; padding:10px 12px; background:#f8faf5;
  color:#315b19; font:inherit; font-weight:700; text-align:left; cursor:pointer;
}
.mealim-analysis-details__trigger:hover { background:#f0f6ec; }
.mealim-analysis-details__trigger:focus-visible { outline:3px solid #83c51f; outline-offset:2px; }
.mealim-analysis-details__body { padding-top:12px; }
.mealim-tip { margin:0 0 12px; padding:10px 12px; border-radius:9px; background:#f5f1e8; color:#48534a; font-size:14px; line-height:1.45; }
.mealim-grid { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:10px; }
.mealim-diagnostic { display:grid; grid-template-columns:24px minmax(0,1fr); gap:9px; padding:12px; border-radius:10px; background:#eef7ef; }
.mealim-diagnostic--watch { background:#edf5ff; }
.mealim-diagnostic--improve { background:#fff0eb; }
.mealim-diagnostic--limited { background:#f2f3f1; }
.mealim-icon { width:22px; height:22px; display:grid; place-items:center; border-radius:50%; background:#cce8d1; color:#27613c; font-weight:900; }
.mealim-diagnostic--watch .mealim-icon { background:#c9def8; color:#285789; }
.mealim-diagnostic--improve .mealim-icon { background:#f2c0b2; color:#8d3827; }
.mealim-diagnostic--limited .mealim-icon { background:#e1e4e0; color:#526055; }
.mealim-diagnostic__body { min-width:0; }
.mealim-heading { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:8px; min-width:0; }
.mealim-heading h4 { flex:1 1 150px; min-width:0; margin:1px 0 0; color:#34463a; font-size:13px; line-height:1.35; }
.mealim-status { flex:0 1 auto; max-width:100%; padding:4px 7px; border-radius:12px; background:#cce8d1; color:#27613c; font-size:9px; font-weight:800; white-space:normal; overflow-wrap:anywhere; }
.mealim-diagnostic--watch .mealim-status { background:#c9def8; color:#285789; }
.mealim-diagnostic--improve .mealim-status { background:#f2c0b2; color:#8d3827; }
.mealim-diagnostic--limited .mealim-status { background:#e1e4e0; color:#526055; }
.mealim-diagnostic p { margin:7px 0 0; color:#4f5953; font-size:12px; line-height:1.45; }
.mealim-diagnostic details { margin-top:8px; border-top:1px solid rgba(40,60,48,.12); padding-top:7px; }
.mealim-diagnostic summary { cursor:pointer; color:#315b19; font-size:10px; font-weight:800; text-decoration:underline; }
.mealim-diagnostic details p { font-size:11px; line-height:1.5; }
.mealim-note { margin:10px 0 0; color:#68756c; font-size:11px; line-height:1.4; }
.mealim-message { margin:0; padding:12px; border-radius:9px; background:#f5f1e8; color:#48534a; font-size:13px; line-height:1.5; }
.mealim-message p { margin:0 0 5px; }
.mealim-message--error { background:#fff0eb; color:#8d3827; }
@media(max-width:720px) {
  .mealim-grid { grid-template-columns:1fr; }
  .mealim-heading { align-items:flex-start; flex-direction:column; }
  .mealim-status { align-self:flex-start; }
}
</style>
